import { identity, isEmpty, omit, pickBy } from 'lodash';
import { UserType } from '../enum';

export const camelCaseToText = (str: string | undefined) =>
  str ? `${str[0].toUpperCase()}${str.slice(1).replace(/[A-Z]/g, (letter) => ` ${letter}`)}` : '';

export const dataUrlToFile = async (dataUrl: string, fileName: string): Promise<File> => {
  const res: Response = await fetch(dataUrl);
  const blob: Blob = await res.blob();
  return new File([blob], fileName, { type: 'image/png' });
};

export const filterKeyGenerator = (filters?: { [key: string]: any }) => {
  if (!filters) return 'empty';
  const omitFilters = omit(filters, ['after', 'before']);
  const pickFilters = pickBy(omitFilters, identity);

  if (isEmpty(pickFilters)) return 'empty';

  const ordered = Object.keys(pickFilters)
    .sort()
    .reduce((obj: any, key) => {
      obj[key] = pickFilters[key];
      return obj;
    }, {});
  return JSON.stringify(ordered);
};

export const filterDecoded = (filterKey?: string) => {
  if (filterKey === 'empty' || !filterKey) return {};

  return JSON.parse(filterKey);
};

export const calculatorAge = (birthday: Date, today: number = new Date().getTime()) => {
  const birthDate = new Date(birthday).getTime();

  const calc = new Date(Math.abs(today - birthDate));

  const daysPassed = Number(Math.abs(calc.getDate()) - 1);
  const monthsPassed = Number(Math.abs(calc.getMonth() + 1) - 1);
  const yearsPassed = Number(Math.abs(calc.getFullYear()) - 1970);

  // Set up custom text
  const yearsTxt = ['year', 'years'];
  const monthsTxt = ['month', 'months'];
  const daysTxt = ['day', 'days'];

  const result =
    (yearsPassed !== 0 ? `${yearsPassed} ${yearsTxt[yearsPassed > 1 ? 1 : 0]} ` : '') +
    (monthsPassed !== 0 ? `${monthsPassed} ${monthsTxt[monthsPassed > 1 ? 1 : 0]} ` : '') +
    (daysPassed !== 0 ? `${daysPassed} ${daysTxt[daysPassed > 1 ? 1 : 0]} ` : '');

  return daysPassed || monthsPassed || yearsPassed ? result.trim() : '0 day';
};

export const convertPhoneNumber = (phoneCode: string, phoneNumber: string) => {
  let result: string = phoneNumber;

  if (result.startsWith('0')) {
    result = result.substring(1);
  }

  if (result.startsWith(phoneCode)) {
    return result;
  }

  return phoneCode + result;
};

export const convertResendOtpTime = (time: number) => {
  const minute = Math.floor(time / 60000);
  const second = Math.round((time % 60000) / 1000);
  return { minute, second };
};

// eslint-disable-next-line no-promise-executor-return
export const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const convertMmToPx = (mm: number) => mm * 3.78;

export const convertPxToMm = (px: number) => px / 3.78;

export const convertPxToPt = (px: number) => px * 0.75;

export const convertHexToRGBA = (hexCode: string) => {
  let hex = hexCode.replace('#', '');
  let opacity = 100;

  if (hex.length > 6) {
    const str = hex.substring(6);
    opacity = Number(str) || 100;
  }
  if (hex.length === 3) {
    hex = `${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`;
  }

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  return {
    r,
    g,
    b,
    a: opacity / 100,
  };
};

export const readAsDataURL = async (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onerror = reject;
    fr.onload = () => {
      resolve(fr.result as string);
    };
    fr.readAsDataURL(file);
  });
};

export const timeSince = (date: number) => {
  const seconds = Math.floor((new Date().getTime() - date) / 1000);

  let interval = seconds / 31536000;

  if (interval > 1) {
    return `${Math.floor(interval)}y ago`;
  }
  interval = seconds / 2592000;
  if (interval > 1) {
    return `${Math.floor(interval)}m ago`;
  }
  interval = seconds / 86400;
  if (interval > 1) {
    return `${Math.floor(interval)}d ago`;
  }
  interval = seconds / 3600;
  if (interval > 1) {
    return `${Math.floor(interval)}h ago`;
  }
  interval = seconds / 60;
  if (interval > 1) {
    return `${Math.floor(interval)}m ago`;
  }
  return `${Math.floor(seconds)}s ago`;
};

export const renderUnit = (size: string, unit: string, ratio: number) => {
  return Number.isNaN(Number(size.split(unit)[0] || 0)) ? '' : `${Number(size.split(unit)[0] || 0) * ratio}${unit}`;
};

export const changeOrder = (arr: any[], oldIndex: number, newIndex: number) => {
  if (newIndex >= arr.length) {
    let k = newIndex - arr.length + 1;
    // eslint-disable-next-line no-plusplus
    while (k--) {
      arr.push(undefined);
    }
  }
  arr.splice(newIndex, 0, arr.splice(oldIndex, 1)[0]);
  return arr;
};

export const parseNumber = (value: string, defaultValue: number = 0, { min, max }: { min?: number; max?: number } = {}) => {
  const number = Number(value);

  if (Number.isNaN(number) || (min !== undefined && number < min) || (max !== undefined && number > max)) {
    return defaultValue;
  }

  return number;
};

export const getUserRole = (role: string): UserType | null => {
  switch (role) {
    case 'designer':
      return UserType.DESIGNER;
    case 'admin':
      return UserType.ADMIN;
    case 'user':
      return UserType.USER;
    default:
      return null;
  }
};
